import React, { useState, useEffect } from 'react';
import { CommonArea, CommonAreaFormData } from '../types/commonArea';
import FormSlider from './FormSlider';
import api from '../services/api';
import '../styles/DrawerForm.css';
import '../styles/Buttons.css';

interface CommonAreaFormSliderProps {
  workspaceId: string;
  commonAreaId?: string;
  onSuccess: () => void;
  onCancel: () => void;
}

interface Builder {
  _id: string;
  name: string;
}

interface Project {
  _id: string;
  name: string;
  builderId: string;
}

const CommonAreaFormSlider: React.FC<CommonAreaFormSliderProps> = ({
  workspaceId,
  commonAreaId,
  onSuccess,
  onCancel
}) => {
  const [builders, setBuilders] = useState<Builder[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loadingBuilders, setLoadingBuilders] = useState(true);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [initialBuilderId, setInitialBuilderId] = useState('');
  const [initialProjectId, setInitialProjectId] = useState('');
  const [editModeBuilderIdLoaded, setEditModeBuilderIdLoaded] = useState('');
  const [editModeProjectName, setEditModeProjectName] = useState('');
  const [levels, setLevels] = useState<string[]>([]);
  const [loadingLevels, setLoadingLevels] = useState(false);

  const initialFormData: CommonAreaFormData = {
    builderId: initialBuilderId,
    projectId: initialProjectId,
    areaName: '',
    level: '',
    area: '',
    buildingType: '',
    areaType: '',
    status: 'ACTIVE',
    description: '',
    capacity: '',
    operatingHours: '',
    isAccessible: true,
    requiresKeyAccess: false
  };

  const fetchProjects = async (builderId: string, autoSelect: boolean = true) => {
    try {
      setLoadingProjects(true);
      const response = await api.get(`/workspaces/${workspaceId}/projects`, {
        
        params: {
          builderId,
          limit: 1000,
          sortBy: 'name',
          sortOrder: 'asc'
        }
      });
      const projectsList = response.data.projects;
      setProjects(projectsList);

      // Auto-select first project only if autoSelect is true
      if (autoSelect && !commonAreaId && projectsList.length > 0) {
        const firstProjectId = projectsList[0]._id;
        setInitialProjectId(firstProjectId);
        setSelectedProjectId(firstProjectId);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoadingProjects(false);
    }
  };

  const fetchLevels = async (projectId: string) => {
    if (!projectId) {
      setLevels([]);
      return;
    }

    try {
      setLoadingLevels(true);
      const response = await api.get(`/workspaces/${workspaceId}/common-areas/by-project/${projectId}/levels`);
      const levelsList = response.data.levels || [];
      setLevels(levelsList);
    } catch (error) {
      console.error('Error fetching levels:', error);
      setLevels([]);
    } finally {
      setLoadingLevels(false);
    }
  };

  const fetchBuilders = async () => {
    try {
      setLoadingBuilders(true);
      const response = await api.get(`/workspaces/${workspaceId}/builders`, {

        params: {
          limit: 1000,
          sortBy: 'name',
          sortOrder: 'asc'
        }
      });
      const buildersList = response.data.builders;
      setBuilders(buildersList);

      // Auto-select first builder if creating new common area
      if (!commonAreaId && buildersList.length > 0) {
        const firstBuilderId = buildersList[0]._id;
        setInitialBuilderId(firstBuilderId);
        fetchProjects(firstBuilderId, true); // true = auto-select first project
      }
    } catch (error) {
      console.error('Error fetching builders:', error);
    } finally {
      setLoadingBuilders(false);
    }
  };

  useEffect(() => {
    fetchBuilders();
  }, [workspaceId]);

  // Fetch projects when editModeBuilderIdLoaded is set (after form data loads in edit mode)
  useEffect(() => {
    if (editModeBuilderIdLoaded && commonAreaId) {
      fetchProjects(editModeBuilderIdLoaded, false);
    }
  }, [editModeBuilderIdLoaded]);

  // Fetch levels when selectedProjectId changes
  useEffect(() => {
    if (selectedProjectId) {
      fetchLevels(selectedProjectId);
    }
  }, [selectedProjectId]);

  const validateForm = (data: CommonAreaFormData): string | null => {
    if (!data.builderId) {
      return 'Builder selection is required';
    }
    if (!data.projectId) {
      return 'Project selection is required';
    }
    if (!data.areaName.trim()) {
      return 'Common area name is required';
    }
    if (!data.level.trim()) {
      return 'Floor/Level is required';
    }
    return null;
  };

  const transformFetchData = (response: any): CommonAreaFormData => {
    const area: CommonArea = response.commonArea;

    // Set the builderId to trigger project fetch in edit mode
    if (area.builderId) {
      setEditModeBuilderIdLoaded(area.builderId);
    }

    // Store project name to show temporarily while projects list is loading
    if (area.projectName) {
      setEditModeProjectName(area.projectName);
    }

    return {
      builderId: area.builderId || '',
      projectId: area.projectId || '',
      areaName: area.areaName || '',
      level: area.level || '',
      area: area.area?.toString() || '',
      buildingType: area.buildingType || '',
      areaType: area.areaType || '',
      status: area.status || 'ACTIVE',
      description: area.description || '',
      capacity: area.capacity?.toString() || '',
      operatingHours: area.operatingHours || '',
      isAccessible: area.isAccessible !== undefined ? area.isAccessible : true,
      requiresKeyAccess: area.requiresKeyAccess || false
    };
  };

  const transformSubmitData = (data: CommonAreaFormData) => {
    return {
      builderId: data.builderId,
      projectId: data.projectId,
      areaName: data.areaName.trim(),
      level: data.level.trim(),
      area: data.area ? parseFloat(data.area) : undefined,
      buildingType: data.buildingType || undefined,
      areaType: data.areaType || undefined,
      status: data.status,
      description: data.description?.trim() || undefined,
      capacity: data.capacity ? parseInt(data.capacity) : undefined,
      operatingHours: data.operatingHours?.trim() || undefined,
      isAccessible: data.isAccessible,
      requiresKeyAccess: data.requiresKeyAccess
    };
  };

  if (!commonAreaId && loadingBuilders) {
    return (
      <div className="builders-loading" style={{ padding: '64px 0' }}>
        <div className="builders-loading-spinner" />
      </div>
    );
  }

  return (
    <FormSlider
      key={`${initialBuilderId}-${initialProjectId}`}
      workspaceId={workspaceId}
      itemId={commonAreaId}
      onSuccess={onSuccess}
      onCancel={onCancel}
      apiEndpoint={`/workspaces/${workspaceId}/common-areas`}
      initialFormData={initialFormData}
      validateForm={validateForm}
      transformFetchData={transformFetchData}
      transformSubmitData={transformSubmitData}
      formId="common-area-form"
    >
      {({ formData, handleInputChange, handleFieldChange, loading }) => (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
            {/* Builder */}
            <div className="form-group">
              <label htmlFor="builderId" className="form-label">
                Builder
              </label>
              {loadingBuilders ? (
                <div className="builders-loading" style={{ padding: '20px 0', textAlign: 'center' }}>
                  <div className="builders-loading-spinner" />
                </div>
              ) : (
                <select
                  id="builderId"
                  name="builderId"
                  className="form-input"
                  value={formData.builderId}
                  onChange={(e) => {
                    handleInputChange(e);
                    setSelectedProjectId('');
                    setProjects([]);
                    setEditModeProjectName(''); // Clear the cached project name
                    if (e.target.value) {
                      fetchProjects(e.target.value, false); // false = don't auto-select first project
                    }
                  }}
                  required
                  disabled={loading}
                >
                  {!commonAreaId && <option value="">Select Builder</option>}
                  {builders.map((builder) => (
                    <option key={builder._id} value={builder._id}>
                      {builder.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Project */}
            <div className="form-group">
              <label htmlFor="projectId" className="form-label">
                Project
              </label>
              {loadingProjects ? (
                <div className="builders-loading" style={{ padding: '20px 0', textAlign: 'center' }}>
                  <div className="builders-loading-spinner" />
                </div>
              ) : (
                <select
                  id="projectId"
                  name="projectId"
                  className="form-input"
                  value={formData.projectId}
                  onChange={(e) => {
                    handleInputChange(e);
                    setSelectedProjectId(e.target.value);
                    // Clear level field when project changes
                    handleFieldChange('level', '');
                    // Clear levels array and fetch new ones
                    setLevels([]);
                    if (e.target.value) {
                      fetchLevels(e.target.value);
                    }
                  }}
                  required
                  disabled={loading || !formData.builderId || loadingProjects}
                >
                  {!commonAreaId && <option value="">Select Project</option>}
                  {commonAreaId && formData.projectId && projects.length === 0 && editModeProjectName && (
                    <option value={formData.projectId}>{editModeProjectName}</option>
                  )}
                  {projects.map((project) => (
                    <option key={project._id} value={project._id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
            {/* Floor/Level */}
            <div className="form-group">
              <label htmlFor="level" className="form-label">
                Floor/Level
              </label>
              <select
                id="level"
                name="level"
                className="form-input"
                value={formData.level}
                onChange={handleInputChange}
                required
                disabled={loading || !formData.projectId || loadingLevels}
              >
                <option value="">
                  {!formData.projectId ? 'Please select a project first' : loadingLevels ? 'Loading levels...' : 'Select a level'}
                </option>
                {levels.map((level) => (
                  <option key={level} value={level}>
                    {level}
                  </option>
                ))}
                {/* Allow custom level entry if it doesn't exist in the list */}
                {formData.level && !levels.includes(formData.level) && (
                  <option value={formData.level}>
                    {formData.level} (custom)
                  </option>
                )}
              </select>
            </div>

            {/* Common Area Name */}
            <div className="form-group">
              <label htmlFor="areaName" className="form-label">
                Common Area Name
              </label>
              <input
                type="text"
                id="areaName"
                name="areaName"
                className="form-input"
                value={formData.areaName}
                onChange={handleInputChange}
                required
                disabled={loading}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
            {/* Area */}
            <div className="form-group">
              <label htmlFor="area" className="form-label">
                Area (Sq.Ft)
              </label>
              <input
                type="number"
                id="area"
                name="area"
                className="form-input"
                value={formData.area}
                onChange={handleInputChange}
                disabled={loading}
                step="0.01"
              />
            </div>

            {/* Building Type */}
            <div className="form-group">
              <label htmlFor="buildingType" className="form-label">
                Building Type
              </label>
              <select
                id="buildingType"
                name="buildingType"
                className="form-input"
                value={formData.buildingType}
                onChange={handleInputChange}
                disabled={loading}
              >
                <option value=""></option>
                <option value="Condominium">Condominium</option>
                <option value="Townhouse">Townhouse</option>
                <option value="Detached">Detached</option>
                <option value="Semi-Detached">Semi-Detached</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
            {/* Area Type */}
            <div className="form-group">
              <label htmlFor="areaType" className="form-label">
                Area Type:
              </label>
              <select
                id="areaType"
                name="areaType"
                className="form-input"
                value={formData.areaType}
                onChange={handleInputChange}
                disabled={loading}
              >
                <option value=""></option>
                <option value="AMENITY">Amenity</option>
                <option value="UTILITY">Utility</option>
                <option value="CIRCULATION">Circulation</option>
                <option value="PARKING">Parking</option>
                <option value="STORAGE">Storage</option>
                <option value="OTHER">Other</option>
              </select>
            </div>

            {/* Status */}
            <div className="form-group">
              <label htmlFor="status" className="form-label">
                Status:
              </label>
              <select
                id="status"
                name="status"
                className="form-input"
                value={formData.status}
                onChange={handleInputChange}
                disabled={loading}
              >
                <option value="ACTIVE">Active</option>
                <option value="UNDER_CONSTRUCTION">Under Construction</option>
                <option value="PLANNED">Planned</option>
                <option value="INACTIVE">Inactive</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
            {/* Capacity */}
            <div className="form-group">
              <label htmlFor="capacity" className="form-label">
                Capacity:
              </label>
              <input
                type="number"
                id="capacity"
                name="capacity"
                className="form-input"
                value={formData.capacity}
                onChange={handleInputChange}
                disabled={loading}
                min="0"
              />
            </div>

            {/* Operating Hours */}
            <div className="form-group">
              <label htmlFor="operatingHours" className="form-label">
                Operating Hours:
              </label>
              <input
                type="text"
                id="operatingHours"
                name="operatingHours"
                className="form-input"
                value={formData.operatingHours}
                onChange={handleInputChange}
                disabled={loading}
                placeholder="e.g., 9 AM - 5 PM"
              />
            </div>
          </div>

          {/* Description */}
          <div className="form-group">
            <label htmlFor="description" className="form-label">
              Description:
            </label>
            <textarea
              id="description"
              name="description"
              className="form-input"
              value={formData.description}
              onChange={handleInputChange}
              disabled={loading}
              rows={3}
            />
          </div>

          {/* Checkboxes */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  name="isAccessible"
                  checked={formData.isAccessible}
                  onChange={(e) => handleFieldChange('isAccessible', e.target.checked)}
                  disabled={loading}
                />
                <span className="form-label" style={{ margin: 0 }}>Is Accessible</span>
              </label>
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  name="requiresKeyAccess"
                  checked={formData.requiresKeyAccess}
                  onChange={(e) => handleFieldChange('requiresKeyAccess', e.target.checked)}
                  disabled={loading}
                />
                <span className="form-label" style={{ margin: 0 }}>Requires Key Access</span>
              </label>
            </div>
          </div>
        </>
      )}
    </FormSlider>
  );
};

export default CommonAreaFormSlider;
